import { useEffect, useRef, useState } from 'react';

const SESSION_KEY = 'numuv_intro_played';
const LETTERS = [
  { c: 'n', accent: false },
  { c: 'u', accent: false },
  { c: 'm', accent: true },
  { c: 'u', accent: true },
  { c: 'v', accent: false },
];

const removePreload = () => {
  const el = document.getElementById('brand-preload');
  if (el) { el.style.opacity = '0'; setTimeout(() => el.remove(), 400); }
};

const findLogo = () => {
  const nodes = Array.from(document.querySelectorAll('[data-brand-logo]'));
  return nodes.find((n) => {
    const r = n.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.top >= 0 && r.top < window.innerHeight;
  }) || null;
};

// Brand opening animation: the wordmark reveals centre-stage, then morphs onto the
// real top-left logo (measured at runtime) and hands over to the app.
// Once per session, on web and in the native apps.
export default function BrandIntro() {
  const [phase, setPhase] = useState(() =>
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_KEY)) ? 'done' : 'reveal');
  const [font, setFont] = useState(null);
  const [flight, setFlight] = useState(null);
  const markRef = useRef(null);

  useEffect(() => {
    removePreload();
    if (phase === 'done') return;

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduce) { setPhase('done'); return; }

    sessionStorage.setItem(SESSION_KEY, '1');

    // Match the destination logo's typeface so the morph lands invisibly.
    const target = findLogo();
    if (target) {
      const cs = getComputedStyle(target);
      setFont({ fontFamily: cs.fontFamily, fontWeight: cs.fontWeight, letterSpacing: cs.letterSpacing });
    }

    const toFly = setTimeout(() => {
      const logo = findLogo();
      const from = markRef.current?.getBoundingClientRect();
      if (logo && from && from.width) {
        const to = logo.getBoundingClientRect();
        setFlight({
          transform: `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${to.width / from.width})`,
          color: getComputedStyle(logo).color,
        });
      } else if (from) {
        setFlight({ transform: `translate(${26 - from.left}px, ${26 - from.top}px) scale(0.25)` });
      }
      setPhase('fly');
    }, 1250);

    const toDone = setTimeout(() => setPhase('done'), 2150);
    return () => { clearTimeout(toFly); clearTimeout(toDone); };
    // Timers are scheduled exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === 'done') return null;

  return (
    <div
      className={`brand-intro ${phase === 'fly' ? 'brand-intro--fly' : ''}`}
      data-testid="brand-intro"
      aria-hidden="true"
    >
      <div className="brand-intro__glow" />

      <div className="brand-intro__mark" ref={markRef} style={{ ...(font || {}), ...(flight || {}) }}>
        {LETTERS.map((l, i) => (
          <span
            key={i}
            className={`brand-intro__letter ${l.accent ? 'brand-intro__letter--accent' : ''}`}
            style={{ animationDelay: `${i * 90}ms` }}
          >
            {l.c}
          </span>
        ))}
      </div>
      <div className="brand-intro__tagline">AI-native recruitment</div>
    </div>
  );
}
